import { useCallback, useEffect, useState } from "react";
import { getReq } from "../utils/request";
import { MyAiAccess } from "../types/Ai";

const CHANGE_EVENT = "crx:ai-access-changed";
let cached: { value: MyAiAccess | null; at: number } | null = null;
let pending: Promise<MyAiAccess | null> | null = null;
const TTL_MS = 60 * 1000;

const load = async (force = false): Promise<MyAiAccess | null> => {
    if (!force && cached && Date.now() - cached.at < TTL_MS) return cached.value;
    if (!force && pending) return pending;
    pending = Promise.resolve(getReq<MyAiAccess>("ai/me"))
        .then((response) => (response?.status === 200 ? response.body : null))
        .catch(() => null)
        .then((value) => {
            cached = { value, at: Date.now() };
            pending = null;
            return value;
        });
    return pending;
};

/** Tell every useAiAccess user to read ai/me again (after a question was asked). */
export const refreshAiAccess = async (): Promise<void> => {
    await load(true);
    window.dispatchEvent(new Event(CHANGE_EVENT));
};

/**
 * The signed-in user's AI access (ai/me): whether the assistant and AI
 * buttons show, Smart, and today's use. Null while loading or when AI
 * isn't available — callers render nothing then.
 */
export const useAiAccess = (): { access: MyAiAccess | null; refresh: () => Promise<void> } => {
    const [access, setAccess] = useState<MyAiAccess | null>(cached?.value ?? null);

    useEffect(() => {
        let isMounted = true;
        const update = () => load().then((value) => { if (isMounted) setAccess(value); });
        update();
        window.addEventListener(CHANGE_EVENT, update);
        return () => {
            isMounted = false;
            window.removeEventListener(CHANGE_EVENT, update);
        };
    }, []);

    const refresh = useCallback(() => refreshAiAccess(), []);
    return { access, refresh };
};
